import { z } from 'zod';
import { ActorSchema } from '../common/actor.schema';
import { DocumentCategorySchema } from '../property/documents.schemas';
import { DossierConsumerSchema } from '../property/dossier.schemas';

/**
 * Emitted when a document analysis reaches COMPLETED (IMM-500.1). Drives the
 * property-facts derivation: the subscriber loads the named analysis and turns its
 * readings into facts. Carries the analysis + its scope so the subscriber never has
 * to re-resolve which property/workspace the analysis belonged to. `audited: never`
 * — a background derivation trigger with no user-facing surface.
 *
 * The one-off backfill (migration seed) publishes this exact shape per historical
 * `COMPLETED` analysis, so the payload must stay derivable from a
 * `property.document_analyses` row alone.
 */
export const DocumentAnalysisCompletedPayloadSchema = z.object({
  analysisId: z.uuid(),
  documentId: z.uuid(),
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  documentCategory: DocumentCategorySchema,
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DocumentAnalysisCompletedPayload = z.infer<typeof DocumentAnalysisCompletedPayloadSchema>;

/**
 * Emitted when a member corrects one field of a COMPLETED document analysis
 * (IMM-1051). Drives the same facts derivation as
 * `property.document-analysis-completed`: the correction changed a reading, and
 * the facts layer is a derivation over the readings (IMM-500, Resolved Decision 6),
 * so the source's facts are re-derived. A separate leaf from the completed event
 * because a correction is not a completion. Audited (IMM-1095): a member changed
 * extracted data.
 */
export const DocumentAnalysisCorrectedPayloadSchema = z.object({
  analysisId: z.uuid(),
  documentId: z.uuid(),
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  fieldKey: z.string().min(1),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DocumentAnalysisCorrectedPayload = z.infer<typeof DocumentAnalysisCorrectedPayloadSchema>;

/**
 * Emitted after the facts of a property change — at least one fact written or
 * superseded by a derivation (IMM-500.1) or by the removal of its source document
 * (IMM-1052). Identity-only payload; a consumer re-reads
 * the property's live facts rather than trusting values on the event. `audited:
 * never` — high-volume, no audit value.
 *
 * No subscriber today: the dossier brief used to regenerate from this event and now
 * refreshes itself on read instead (see `DossierBriefStalePayloadSchema`). The event
 * stays because it is IMM-500.1's contract and the honest signal for "this
 * property's facts moved" — the next consumer that needs it should not have to
 * reintroduce it.
 */
export const PropertyFactsChangedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PropertyFactsChangedPayload = z.infer<typeof PropertyFactsChangedPayloadSchema>;

/**
 * Emitted when the valuation agent's findings are stored (IMM-1391). Ids and counts only: the values are
 * on the fact rows, and the audit trail records that the write happened and which rows it made.
 */
export const PropertyAgentFindingsWrittenPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  factIds: z.array(z.uuid()).min(1),
  supersededCount: z.number().int().nonnegative(),
  refusedCount: z.number().int().nonnegative(),
  agentTokenId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PropertyAgentFindingsWrittenPayload = z.infer<typeof PropertyAgentFindingsWrittenPayloadSchema>;

/**
 * Emitted when an operator adds a catalog entry (IMM-1383.4). Audited; no subscriber.
 */
export const PropertyFactKeyCreatedPayloadSchema = z.object({
  key: z.string(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PropertyFactKeyCreatedPayload = z.infer<typeof PropertyFactKeyCreatedPayloadSchema>;

/**
 * Emitted when an operator retires a catalog entry (IMM-1383.5). Audited; no subscriber.
 */
export const PropertyFactKeyRetiredPayloadSchema = z.object({
  key: z.string(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PropertyFactKeyRetiredPayload = z.infer<typeof PropertyFactKeyRetiredPayloadSchema>;

/**
 * Emitted when an operator edits a taxonomy key's flags — its `publicSafe` or
 * `scope` (IMM-500.5). Drives the dossier-brief DROP subscriber (IMM-500.3): every
 * cached brief of a property holding a live fact on this key is deleted, because a
 * brief written under the old flags can contradict the new ones — an Exposé brief
 * still describing a key an operator has just marked unsafe is exactly the leak the
 * feature exists to prevent. Dropping (not regenerating) keeps one operator toggle
 * from fanning out into a model call per affected property.
 *
 * Published by the operator fact-key edit when a dossier-affecting flag changes.
 * Audited (IMM-1095): it is the only event the edit emits. A label-only edit
 * publishes nothing and so leaves no audit row.
 */
export const PropertyFactKeyUpdatedPayloadSchema = z.object({
  key: z.string(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PropertyFactKeyUpdatedPayload = z.infer<typeof PropertyFactKeyUpdatedPayloadSchema>;

/**
 * Emitted by the dossier READ when the projection it just composed no longer matches
 * the hash the stored brief was generated from — the brief is stale (or absent) and
 * wants regenerating.
 *
 * This is what makes the brief a read-through cache rather than a write-through one.
 * Generation cost now tracks reads, not writes: a property edited thirty times and
 * never read costs nothing, where the write-driven design paid a model call per edit
 * per projection. It is also what the parent PRD asks for in as many words —
 * „invalidated and regenerated **on next request**".
 *
 * Carries the `consumer`, so only the projection actually being read is regenerated;
 * an `EXPOSE` brief is never paid for until something asks for one. The read never
 * blocks on the model: it serves whatever it has (possibly stale, possibly null) and
 * publishes this. `audited: never` — a cache-maintenance trigger, not a domain fact.
 */
export const DossierBriefStalePayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  consumer: DossierConsumerSchema,
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DossierBriefStalePayload = z.infer<typeof DossierBriefStalePayloadSchema>;
